/**
 * @file 讲义试卷数据
 * @description 把三份本地试卷 JSON 转成打印模板入参。前三条模拟申请分别对应三套模板。
 *
 * @whenToUse 讲义打印预览取数。
 * @whenNotToUse 已接入真实试卷接口之后（改为请求结果）。
 */

import QRCode from 'qrcode'
import paper1Json from '@/assets/paper1.json'
import paper2Json from '@/assets/paper2.json'
import paper3Json from '@/assets/paper3.json'
import type { ChoiceOption } from '@/components/pages/handout-page/print/blocks.tsx'
import type { HandoutPaperKind, HandoutPrintRow } from '@/components/pages/handout-page/types.ts'
import {
  QuestionType,
  Subject,
  educationStageMap,
  gradeMap,
  learningLevelMap,
  questionTypeMap,
  semesterMap,
  subjectMap,
} from '@/enum/common.ts'

/** 未配置环境变量时的答案二维码页 */
const DEFAULT_QR_BASE = 'https://sibenmobile.nbxyz.xyz/paper'

/** 模板题目 */
export type PaperQuestion = {
  /** 题干 HTML */
  questionHtml: string
  /** 答案 HTML */
  answerHtml: string
  /** 讲解 HTML */
  analysisHtml: string
  /** 题型 */
  type?: QuestionType
  /** 选择题选项 */
  options?: ChoiceOption[]
}

/** 一份可打印的试卷 */
export type HandoutPaperModel = {
  /** 模板 */
  kind: Exclude<HandoutPaperKind, ''>
  /** 页眉 */
  header: {
    paperNo: string
    studentName: string
    phone: string
    subjectLabel: string
    stageLabel: string
    gradeLabel: string
    semesterLabel: string
    learningForce: string
    learningType: string
    textbookName: string
  }
  /** 核心知识点 HTML */
  coreKnowledgePoints: string[]
  /** 巩固记忆题干 HTML */
  consolidationQuestions: string[]
  /** 常考题型 / 典例 */
  learnWithMeList: PaperQuestion[]
  /** 牛刀小试 */
  tryPracticeQuestions: PaperQuestion[]
  /** 巩固练习 */
  adaptivePracticeQuestions: PaperQuestion[]
  /** 阶段卷题目（仅阶段卷模板使用） */
  stageQuestions: PaperQuestion[]
  /** 是否渲染答案页、看答案流程与答案二维码 */
  printAnswers: boolean
}

type QuestionDetailPayload = {
  /** 题干 */
  questionContent?: string
  /** 答案 */
  answerContent?: string
  /** 讲解 */
  analysisContent?: string
  /** 选项 */
  options?: { label: string; value: string }[]
  /** 题型 */
  type?: string
}

type KnowledgeQuestion = {
  /** 学科 */
  subject?: string
  /** 题型 */
  type?: string
  /** 题目详情 JSON 字符串 */
  questionDetail?: string
  /** 创建时间 */
  createTime?: string
}

type KnowledgePoint = {
  /** 学科 */
  subject?: string
  /** 知识点 HTML */
  knowledgePoint?: string
  /** 知识点片段 */
  knowledgePointJson?: string[] | null
  /** 创建时间 */
  createTime?: string
}

type PaperItem = {
  /** 学案编号 */
  paperNo?: string
  /** 核心知识点 */
  coreKnowledgePoints?: KnowledgePoint[] | null
  /** 巩固记忆 */
  consolidationQuestions?: KnowledgeQuestion[] | null
  /** 典例精学 */
  learnWithMeQuestions?: KnowledgeQuestion[] | null
  /** 牛刀小试 */
  tryPracticeQuestions?: KnowledgeQuestion[] | null
  /** 巩固练习 / 阶段卷题目 */
  adaptivePracticeQuestions?: KnowledgeQuestion[] | null
}

type PaperFile = {
  /** 试卷列表 */
  data?: PaperItem[]
}

const PAPER_FILES: Record<Exclude<HandoutPaperKind, ''>, PaperFile> = {
  knowledge: paper1Json as PaperFile,
  stage: paper2Json as PaperFile,
  'knowledge-lite': paper3Json as PaperFile,
}

/**
 * 解析题目详情 JSON。
 * @param detail 接口里的 questionDetail 字符串
 */
function parseDetail(detail?: string): QuestionDetailPayload | null {
  if (!detail) return null
  try {
    return JSON.parse(detail) as QuestionDetailPayload
  } catch {
    return null
  }
}

/**
 * 识别题型枚举。
 * @param value 原始题型
 */
function asQuestionType(value?: string): QuestionType | undefined {
  if (
    value === QuestionType.FILL_BLANK ||
    value === QuestionType.SHORT_ANSWER ||
    value === QuestionType.APPLICATION ||
    value === QuestionType.SELECT
  ) {
    return value
  }
  return undefined
}

/**
 * 题目列表转为模板题目。
 * @param list 原始题目
 */
function readQuestions(list?: KnowledgeQuestion[] | null): PaperQuestion[] {
  if (!list) return []
  const result: PaperQuestion[] = []
  for (const question of list) {
    const detail = parseDetail(question.questionDetail)
    if (!detail?.questionContent) continue
    const type = asQuestionType(question.type ?? detail.type)
    const options = (detail.options ?? [])
      .filter((option) => option.value)
      .map((option) => ({ label: option.label, valueHtml: option.value }))
    result.push({
      questionHtml: detail.questionContent,
      answerHtml: detail.answerContent ?? '',
      analysisHtml: detail.analysisContent ?? '',
      type,
      options: type === QuestionType.SELECT && options.length > 0 ? options : undefined,
    })
  }
  return result
}

/**
 * 巩固记忆题干（含题型与选项文本）。
 * @param list 巩固记忆题目
 */
function readConsolidation(list?: KnowledgeQuestion[] | null): string[] {
  if (!list) return []
  return list.flatMap((question) => {
    const detail = parseDetail(question.questionDetail)
    if (!detail?.questionContent) return []
    const type = asQuestionType(question.type ?? detail.type)
    const typeLabel = type ? questionTypeMap[type] : ''
    const typeHtml = typeLabel ? `<span class="question-type">[${typeLabel}]</span>` : ''
    const options = detail.options ?? []
    const optionsHtml = options.some((option) => option.value)
      ? `<p>${options
          .map((option) => `${option.label}${option.value ? `．${option.value}` : ''}`)
          .join('&nbsp;&nbsp;')}</p>`
      : ''
    return [`${typeHtml}${detail.questionContent}${optionsHtml}`]
  })
}

/**
 * 核心知识点按创建时间排序。
 * @param list 知识点
 */
function readCorePoints(list?: KnowledgePoint[] | null): string[] {
  if (!list) return []
  return [...list]
    .sort((left, right) => {
      const leftTime = left.createTime ? Date.parse(left.createTime) : Number.POSITIVE_INFINITY
      const rightTime = right.createTime ? Date.parse(right.createTime) : Number.POSITIVE_INFINITY
      return leftTime - rightTime
    })
    .map((point) => {
      const fromJson = Array.isArray(point.knowledgePointJson)
        ? point.knowledgePointJson.join('')
        : ''
      return fromJson || point.knowledgePoint || ''
    })
    .filter(Boolean)
}

/**
 * 从题目上取学科文案。
 * @param paper 试卷
 */
function subjectLabelOf(paper: PaperItem): string {
  const pools = [
    paper.coreKnowledgePoints,
    paper.consolidationQuestions,
    paper.learnWithMeQuestions,
    paper.tryPracticeQuestions,
    paper.adaptivePracticeQuestions,
  ]
  for (const pool of pools) {
    const subject = pool?.find((item) => item.subject)?.subject
    if (subject && subject in subjectMap) return subjectMap[subject as Subject]
  }
  return subjectMap[Subject.MATH]
}

/**
 * 生成试卷答案二维码。
 * @param paperNo 学案编号
 */
export async function buildPaperAnswerQrCodeDataUrl(paperNo: string): Promise<string> {
  const base = import.meta.env.VITE_APP_PAPER_ANSWER_QRCODE_URL || DEFAULT_QR_BASE
  const url = new URL(base, window.location.origin)
  url.searchParams.set('no', paperNo)
  return QRCode.toDataURL(url.toString(), {
    errorCorrectionLevel: 'M',
    margin: 1,
    width: 200,
    color: { dark: '#000000', light: '#FFFFFF' },
  })
}

/**
 * 按申请行组装预览数据。没有绑定模板时返回 null。
 * @param row 打印申请
 * @param printAnswers 是否渲染答案相关内容
 */
export function buildHandoutPaper(
  row: HandoutPrintRow,
  printAnswers: boolean,
): HandoutPaperModel | null {
  if (!row.paperKind) return null
  const paper = PAPER_FILES[row.paperKind].data?.[0]
  if (!paper) return null
  const learnWithMeList = readQuestions(paper.learnWithMeQuestions)
  const tryPracticeQuestions = readQuestions(paper.tryPracticeQuestions)
  const adaptivePracticeQuestions = readQuestions(paper.adaptivePracticeQuestions)
  return {
    kind: row.paperKind,
    header: {
      paperNo: paper.paperNo || '',
      studentName: row.studentName,
      phone: '',
      subjectLabel: subjectLabelOf(paper),
      stageLabel: educationStageMap[row.educationStage],
      gradeLabel: gradeMap[row.grade],
      semesterLabel: semesterMap[row.semester],
      learningForce: String(row.learningForce),
      learningType: learningLevelMap[row.learningGrade],
      textbookName: row.textbookName,
    },
    coreKnowledgePoints: readCorePoints(paper.coreKnowledgePoints),
    consolidationQuestions: readConsolidation(paper.consolidationQuestions),
    learnWithMeList,
    tryPracticeQuestions,
    adaptivePracticeQuestions,
    stageQuestions: adaptivePracticeQuestions,
    printAnswers,
  }
}
